// =============================================================================
// api.ts — typed fetch client for the client-portal Worker.
//
// Same-origin in production (VITE_API_URL empty). Local dev proxies /api
// through Vite to a wrangler dev session. The Worker resolves the signed-in
// client from the Cloudflare Access header — there are no client credentials
// to manage here.
// =============================================================================

const BASE = import.meta.env.VITE_API_URL ?? ''

export class ApiError extends Error {
  status: number
  reason: string | undefined
  constructor(message: string, status: number, reason?: string) {
    super(message)
    this.status = status
    this.reason = reason
  }
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include' })
  const body = (await res.json().catch(() => null)) as
    | (Record<string, unknown> & { ok?: boolean; error?: string; reason?: string })
    | null
  if (!res.ok || !body || body.ok === false) {
    const reason = body?.reason
    const err = body?.error ?? `http_${res.status}`
    throw new ApiError(err, res.status, typeof reason === 'string' ? reason : undefined)
  }
  return body as T
}

// Patch 015.12 — the portal's first write path (Welcome Home item complete).
async function send<T>(
  path: string,
  method: string,
  payload?: unknown,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: payload === undefined ? undefined : JSON.stringify(payload),
  })
  const body = (await res.json().catch(() => null)) as
    | (Record<string, unknown> & { ok?: boolean; error?: string; reason?: string })
    | null
  if (!res.ok || !body || body.ok === false) {
    const reason = body?.reason
    const err = body?.error ?? `http_${res.status}`
    throw new ApiError(err, res.status, typeof reason === 'string' ? reason : undefined)
  }
  return body as T
}

// ---- types ------------------------------------------------------------------

export interface MeResponse {
  ok: true
  profile: Record<string, unknown>
  fields_visible: string[]
  display_name: string
}

export interface Project {
  id: string
  display_name: string
  address_street: string | null
  address_city: string | null
  address_state: string | null
  address_zip: string | null
  status: string
  current_phase: string | null
  target_completion_date: string | null
}

export interface ScheduleTask {
  id: string
  display_name: string
  phase: string | null
  schedule_lane: string | null
  status: string
  planned_start: string | null
  planned_finish: string | null
  actual_start: string | null
  actual_finish: string | null
}

export interface Photo {
  id: string
  caption: string | null
  phase: string | null
  taken_at: string | null
  uploaded_at: string
  mime_type: string | null
  file_url: string
}

export interface Selection {
  id: string
  display_name: string
  category: string | null
  status: string
  selection_deadline: string | null
  client_decision_at: string | null
  client_decision_value: string | null
}

export interface ChangeOrder {
  id: string
  display_name: string
  description: string
  amount_cents: number
  affects_schedule: number | null
  schedule_impact_days: number | null
  status: string
  client_signed_at: string | null
  created_at: string
}

export interface Communication {
  id: string
  project_id: string | null
  comm_type: string
  subject: string
  body: string
  sent_at: string | null
}

export type WelcomeHomeItemGroup =
  | 'utilities'
  | 'address_change'
  | 'local'
  | 'heritage'

export interface WelcomeHomeItem {
  id: string
  item_group: WelcomeHomeItemGroup
  display_name: string
  description: string | null
  action_url: string | null
  target_date: string | null
  completed: number
  completed_at: string | null
  completed_by: string | null
  sort_order: number
}

export interface WelcomeHomeResponse {
  ok: true
  items: WelcomeHomeItem[]
  summary: {
    total: number
    completed: number
    groups: Record<WelcomeHomeItemGroup, { total: number; completed: number }>
  }
}

// ---- endpoints --------------------------------------------------------------

export const api = {
  me: () => get<MeResponse>('/api/me'),
  projects: () => get<{ ok: true; projects: Project[] }>('/api/me/projects'),
  communications: () =>
    get<{ ok: true; communications: Communication[] }>('/api/me/communications'),
  schedule: (projectId: string) =>
    get<{ ok: true; tasks: ScheduleTask[] }>(
      `/api/me/projects/${projectId}/schedule`,
    ),
  photos: (projectId: string) =>
    get<{ ok: true; photos: Photo[] }>(`/api/me/projects/${projectId}/photos`),
  selections: (projectId: string) =>
    get<{ ok: true; selections: Selection[] }>(
      `/api/me/projects/${projectId}/selections`,
    ),
  changeOrders: (projectId: string) =>
    get<{ ok: true; change_orders: ChangeOrder[] }>(
      `/api/me/projects/${projectId}/change-orders`,
    ),
  welcomeHome: (projectId: string) =>
    get<WelcomeHomeResponse>(`/api/me/projects/${projectId}/welcome-home`),
  completeWelcomeHomeItem: (
    projectId: string,
    itemId: string,
    completed: boolean,
  ) =>
    send<{ ok: true; id: string; completed: boolean }>(
      `/api/me/projects/${projectId}/welcome-home-items/${itemId}/complete`,
      'PATCH',
      { completed },
    ),
}
